import React from 'react';
import { Helmet } from 'react-helmet-async';

interface ChapterPreviewSEOProps {
  title: string;
  description: string;
  chapterTitle: string;
  storyTitle: string;
  authorName: string;
  coverImageUrl: string;
  chapterId: string;
}

/**
 * SEO component for chapter preview pages
 * 
 * This component adds all necessary meta tags for SEO optimization
 * and social sharing for chapter preview pages.
 */
const ChapterPreviewSEO: React.FC<ChapterPreviewSEOProps> = ({
  title,
  description,
  chapterTitle,
  storyTitle,
  authorName,
  coverImageUrl,
  chapterId,
}) => {
  // Build canonical URL
  const canonicalUrl = `https://app.pulsebooks.com.br/preview/chapter/${chapterId}`;

  // Structured data for rich search results
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": chapterTitle,
    "image": coverImageUrl,
    "author": {
      "@type": "Person",
      "name": authorName
    },
    "publisher": {
      "@type": "Organization",
      "name": "Pulse",
      "logo": {
        "@type": "ImageObject",
        "url": "https://zbvjxryqzrygkixfuidj.supabase.co/storage/v1/object/public/admin_pics/Logos/pulse-vermelho-fundo-transparente-horizontal.png"
      }
    },
    "description": description,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": canonicalUrl
    },
    "datePublished": new Date().toISOString()
  };

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Open Graph Tags for Facebook */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="article" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={coverImageUrl} />
      <meta property="og:site_name" content="Pulse" />
      <meta property="og:locale" content="pt_BR" />
      <meta property="article:author" content={authorName} />
      
      {/* Twitter Card Tags */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:site" content="@pulse" />
      <meta property="twitter:title" content={title} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={coverImageUrl} />
      <meta property="twitter:url" content={canonicalUrl} />
      
      {/* Structured Data for Rich Results */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
};

export default ChapterPreviewSEO; 